import * as Sentry from "@sentry/remix";
import { useEffect } from "react";
import { useLocation, useMatches } from "@remix-run/react";
import {
  baseSentryConfig,
  createTraceTailSampler,
} from "#app/utils/sentry.shared.js";

// Client-side Sentry init, extracted from entry.client.jsx. Call once at module
// load. Mirrors the initPostHog() interface in posthog.client.js. Identity
// fields come from the shared base; reads window.env / window.user (injected by
// document.jsx). Capture calls (captureException etc.) still import
// @sentry/remix directly at their call sites.

const INTERNAL_EMAILS = [
  "danthareja@gmail.com",
  "daniel@schnerp.com",
  "dan@schnerp.com",
];

export function initSentry() {
  if (!window.env || window.env.APP_ENV === "test") return;

  const userEmail = window.user?.email;
  const isInternalUser = userEmail && INTERNAL_EMAILS.includes(userEmail);

  const tailSampler = createTraceTailSampler();

  Sentry.init({
    ...baseSentryConfig(window.env),
    // Tail sampling (see sentry.shared.js): record every trace locally — even
    // when the server's <meta> parent decision says unsampled — because the
    // real keep/drop decision happens at send time in beforeSendTransaction.
    tracesSampler: () => 1,
    beforeSend: (event) => tailSampler.rememberErroredTrace(event),
    beforeSendTransaction: (event) => tailSampler.keepTransaction(event),
    // Unactionable client noise (~50% of web error quota in July 2026).
    // Outage detection lives in Metabase, not in fetch-flake counts.
    ignoreErrors: [
      "Load failed", // WebKit fetch: network drop / page backgrounded
      "Failed to fetch", // Chromium fetch: same transient network failures
      "Object Not Found Matching Id", // Outlook SafeLinks scanner injection
      "window.webkit.messageHandlers", // iOS in-app browser script injection
    ],
    replaysOnErrorSampleRate: 0,
    initialScope: {
      user: window.user || {},
    },
    integrations: [
      // httpClientIntegration creates spans for every fetch - remove to reduce volume
      // Sentry.httpClientIntegration(),
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
        // Only trace slow interactions, not everything
        enableLongTask: false,
        enableInp: false,
      }),
      ...(!isInternalUser
        ? [Sentry.replayIntegration({ maskAllText: false })]
        : []),
    ],
  });
}
